import { isUndefined } from 'lodash-es';
import type { RefObject } from 'react';

import type { RelativeAbsolutePosition } from '../../../../types/pointer-events.js';
import { ZOOM_FACTOR } from '../../constants.js';
import type { InteractionTriggers, ZoomBehavior } from '../../types.js';

export const zoomKeyboardEscape = ({
  selectionArea,
  eventCancelled,
  onSelectEnd,
  lastMousePositionRef,
  pinned,
  onHover,
  onTogglePin,
}: {
  pinned: boolean;
  eventCancelled: RefObject<boolean>;
  lastMousePositionRef: RefObject<RelativeAbsolutePosition | undefined>;
  selectionArea?: [RelativeAbsolutePosition, RelativeAbsolutePosition];
  onHover?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => void;
  onTogglePin?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition | undefined;
    pin: boolean;
  }) => void;
  onSelectEnd?: (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ) => void;
}) => {
  if (!isUndefined(selectionArea)) {
    eventCancelled.current = true;
    onSelectEnd?.({ cancelled: true });

    if (lastMousePositionRef.current) {
      onHover?.({
        triggeredBy: 'pointer',
        position: lastMousePositionRef.current,
      });
    }
  }

  if (pinned) {
    onTogglePin?.({
      triggeredBy: 'keyboard',
      pin: false,
      position: lastMousePositionRef.current,
    });
  }
};

export const zoomKeyboardEnter = ({
  selectionArea,
  lastMousePositionRef,
  width,
  onSelectEnd,
  onZoomChange,
  onHover,
}: {
  lastMousePositionRef: RefObject<RelativeAbsolutePosition | undefined>;
  width: number;
  selectionArea?: [RelativeAbsolutePosition, RelativeAbsolutePosition];

  onSelectEnd?: (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ) => void;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
  onHover?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => void;
}) => {
  if (selectionArea) {
    const deltaStart = selectionArea[0].relative.x;
    const deltaEnd = selectionArea[1].relative.x - width;

    onSelectEnd?.({ cancelled: true });
    onZoomChange?.({
      triggeredBy: 'keyboard',
      deltaStart,
      deltaEnd,
      behavior: 'zoom-x',
    });

    if (lastMousePositionRef.current) {
      onHover?.({
        triggeredBy: 'pointer',
        position: lastMousePositionRef.current,
      });
    }
  }
};

export const zoomKeyboardUp = ({
  width,
  onZoomChange,
}: {
  width: number;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
}) => {
  const delta = Math.floor((width * ZOOM_FACTOR) / 2);
  onZoomChange?.({
    triggeredBy: 'keyboard',
    deltaStart: delta,
    deltaEnd: -delta,
    behavior: 'zoom-in',
  });
};

export const zoomKeyboardDown = ({
  width,
  onZoomChange,
}: {
  width: number;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
}) => {
  const delta = Math.floor((width * ZOOM_FACTOR) / 2);
  onZoomChange?.({
    triggeredBy: 'keyboard',
    deltaStart: -delta,
    deltaEnd: delta,
    behavior: 'zoom-out',
  });
};
